import {
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Popover,
  Row,
  Select,
  Table,
} from "antd";
import COLORS from "common/Colors";
import {
  APPROVAL_FILTER_TYPES,
  MONTH_LIST,
  STATUS_CODE,
} from "common/Constants";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { dateInDetail, getKenyanDateTime } from "utils/Date";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { ButtonFilled } from "reusableComponent/UIButtons";
import {
  DeleteOutlineOutlined,
  EditOutlined,
  Info,
  SearchOutlined,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useTranslation } from "react-i18next";
import { CALL_API } from "common/API";
import { useSelector } from "react-redux";
import { getUserList } from "redux/selectors";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import SuccessDeclinePopup from "reusableComponent/Popup/SuccessDeclinePopup";
import { ReactComponent as Temp } from "../../../assets/template.svg";

dayjs.extend(isSameOrAfter);
dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.extend(advancedFormat);

const HRPerformanceWrap = styled.div`
  .ant-input-affix-wrapper,
  .ant-input,
  .ant-picker,
  .ant-btn {
    border-radius: 24px;
  }

  .ant-select .ant-select-selector {
    border-radius: 24px;
  }
  .ant-input::placeholder {
    color: #000;
  }

  .ant-input:hover::placeholder {
    color: "#2d53da";
  }
  .ant-input::placeholder,
  .ant-picker-input > input::placeholder {
    color: #9ca3af !important;
  }

  .ant-select-selection-placeholder {
    color: #9ca3af !important;
  }
`;

const ActionWrapper = styled.div`
  opacity: 0;
  transition: opacity 0.3s ease;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  color: #2d53da;

  .ant-table-row:hover & {
    opacity: 1;
  }
`;

const StyledTable = styled(Table)`
  .ant-table {
    border: 1px solid #ccc;
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-table-container {
    border-radius: 12px;
  }

  .ant-table-content {
    overflow-x: auto;
  }

  table {
    width: 100%;
    min-width: 900px;
  }

  .ant-table-cell {
    padding: 16px;
    white-space: wrap;
    overflow: hidden;
  }

  .ant-table-thead .ant-table-cell {
    font-weight: 600;
    color: ${COLORS.SECONDARY_BLACK};
  }

  .reason-column {
    min-width: 280px;
    white-space: normal;
  }

  .non-white {
    background: #f9f9f9;
  }

  @media (max-width: 768px) {
    .ant-table-cell {
      padding: 10px;
      font-size: 12px;
    }
  }
`;

const HRPerformance = ({ setKpiDataList, setIsViewKpi, Menulist }) => {
  const history = useHistory();
  const [form] = Form.useForm();
  const containerRef = useRef(null);
  const userList = useSelector(getUserList);
  const { t } = useTranslation();
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);

  const pageTitleRef = useRef(null);
  const [scrollY, setScrollY] = useState(120);

  const handleDelete = async (record) => {
    setDeleteLoading(true);
    try {
      const { code } = await CALL_API(`performance/KPI/${record.id}`, "delete");
      if (code === STATUS_CODE.SUCCESS) {
        setPopupOpen(true);
        setTimeout(() => {
          setKpiDataList({});
          setIsViewKpi(false);
          setPopupOpen(false);
        }, 2000);
      } else {
        message.error("Failed to delete");
      }
    } catch (error) {
      console.error("Delete error:", error);
      message.error("Failed to delete");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Watch all filter values
  const searchName = Form.useWatch("name", form);
  const filterDate = Form.useWatch("date", form);
  const filterType = Form.useWatch("type", form);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data, code } = await CALL_API(`performance/HR/KPIS`, "get");
      if (code === STATUS_CODE.SUCCESS) {
        setDataList(data);
        setLoading(false);
      } else {
        throw new Error(`Failed to fetch data: ${code}`);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!pageTitleRef.current) return;

    const baseOffset = 300;
    const minHeight = 120;

    const calculateScrollY = () => {
      const headerHeight = pageTitleRef.current.offsetHeight;
      const availableHeight = window.innerHeight - headerHeight - baseOffset;
      setScrollY(Math.max(minHeight, availableHeight));
    };

    calculateScrollY();

    const resizeObserver = new ResizeObserver(() => {
      calculateScrollY();
    });

    resizeObserver.observe(pageTitleRef.current);
    window.addEventListener("resize", calculateScrollY);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", calculateScrollY);
    };
  }, []);

  // Apply all filters together
  const filteredData = useMemo(() => {
    let filtered = [...(dataList || [])];

    // 1. Search by appraisee name
    if (searchName && searchName.trim() !== "") {
      filtered = filtered.filter((item) =>
        item?.fullname?.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    // 2. Filter by appraisal date (attendstartdate matches selected date)
    if (filterDate) {
      filtered = filtered.filter((item) => {
        if (!item?.attendstartdate) return false;
        return dayjs(item.attendstartdate).isSame(dayjs(filterDate), "day");
      });
    }

    // 3. Filter by type (multi-select):
    //    "1" = Attended      → attendedon is filled
    //    "2" = Not Attended  → attendedon is empty
    //    "3" = Reviewed      → reviewedon is filled
    //    "4" = Not Reviewed  → reviewedon is empty
    if (filterType && filterType.length > 0) {
      filtered = filtered.filter((item) => {
        const isAttended = !!item?.attendedon;
        const isReviewed = !!item?.reviewedon;

        return filterType.some((type) => {
          if (type === "1") return isAttended;
          if (type === "2") return !isAttended;
          if (type === "3") return isReviewed;
          if (type === "4") return !isReviewed;
          return false;
        });
      });
    }

    return filtered;
  }, [dataList, searchName, filterDate, filterType]);

  const columns = [
    {
      title: "Appraisee",
      dataIndex: "fullname",
      key: "fullname",
    },
    {
      title: "Employee ID",
      dataIndex: "appraiseeuserid",
      key: "appraiseeuserid",
      render: (text) => {
        return userList?.find((val) => val?.userid === text)?.employeeid;
      },
    },
    {
      title: "Appraiser",
      dataIndex: "appraiseruserid",
      key: "appraiseruserid",
      render: (text) => {
        return userList?.find((val) => val?.userid === text)?.employeeid;
      },
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "KPI Issued on",
      dataIndex: "createdat",
      key: "createdat",
      render: (text) => {
        return text ? dayjs(text).format("Do MMM YYYY") : "-";
      },
    },
    {
      title: "Appraisal Date",
      dataIndex: "attendstartdate",
      key: "attendstartdate",
      render: (text) => {
        const today = dayjs();
        const date = dayjs(text);
        const isSame = date.isSame(today, "day");

        return text ? (
          isSame ? (
            <div className="text-danger">{date.format("Do MMM YYYY")}</div>
          ) : (
            date.format("Do MMM YYYY")
          )
        ) : (
          "-"
        );
      },
    },
    {
      title: "Appraisee Attended",
      dataIndex: "attendedon",
      key: "attendedon",
      render: (text) => {
        return text ? dayjs(text).format("Do MMM YYYY") : "-";
      },
    },
    {
      title: "Appraiser Reviewed on",
      dataIndex: "reviewedon",
      key: "reviewedon",
      render: (text) => {
        return text ? dayjs(text, "DD-MM-YYYY").format("Do MMM YYYY") : "-";
      },
    },
    {
      key: "actions",
      width: 130,
      render: (text, record) => {
        const attendanceDate =
          dayjs(record?.attendstartdate, "DD-MM-YYYY") || "";
        const today = dayjs();

        const isPast = attendanceDate.isBefore(today, "day");
        const isSameOrAfterToday = attendanceDate.isSameOrAfter(today, "day");

        return (
          <ActionWrapper>
            {!record?.attendedon && !record?.reviewedon && (
              <>
                {isPast &&
                  Menulist?.find(
                    (item) =>
                      item?.key === "hrperformance" && item?.permission.edit
                  ) && (
                    <EditOutlined
                      onClick={() =>
                        history.push("add-edit-kpi", { data: record })
                      }
                    />
                  )}
                {Menulist?.find(
                  (item) =>
                    item?.key === "hrperformance" && item?.permission.delete
                ) && (
                  <DeleteOutlineOutlined
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      Modal.confirm({
                        title: "Are you sure you want to delete this KPI?",
                        icon: null,
                        centered: true,
                        okText: "Yes",
                        cancelText: "No",
                        okButtonProps: { type: "primary" },
                        onOk: () => handleDelete(record),
                      });
                    }}
                  />
                )}
                {isPast && (
                  <Popover
                    content={
                      <>
                        Appraisees can attend the appraisal and the
                        <br />
                        appraiser can give their reviews right from the
                        <br />
                        appraisal date mentioned.
                      </>
                    }
                    arrow
                  >
                    <Info
                      sx={{
                        color: "#2D53DA",
                        fontSize: "20px",
                        marginRight: "8px",
                        cursor: "pointer",
                      }}
                    />
                  </Popover>
                )}
              </>
            )}
          </ActionWrapper>
        );
      },
    },
  ];

  const validateDateRange = () => {
    const endDate = dayjs(form.getFieldValue("end_date"));
    const startDate = dayjs(form.getFieldValue("start_date"));
    if (dayjs(startDate).isAfter(endDate)) {
      form.setFieldsValue({ end_date: startDate });
    }
  };

  const validateEndDateRange = () => {
    const startDate = dayjs(form.getFieldValue("start_date"));
    const endDate = dayjs(form.getFieldValue("end_date"));
    if (dayjs(endDate).isBefore(startDate)) {
      form.setFieldsValue({ start_date: endDate });
    }
  };

  return (
    <HRPerformanceWrap>
      <div className="page-title" ref={pageTitleRef}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={8} md={8} lg={4} ref={containerRef}>
              <Form.Item name="name">
                <Input
                  placeholder="Search"
                  prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8} md={8} lg={4} ref={containerRef}>
              <Form.Item name="date">
                <DatePicker
                  placeholder="Date"
                  format="DD-MM-YYYY"
                  className="w-100"
                  allowClear
                  getPopupContainer={() => containerRef.current}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8} md={8} lg={4} ref={containerRef}>
              <Form.Item name="type">
                <DropdownCheckBox
                  name="type"
                  label="Type"
                  options={[
                    { label: "Not Attended", value: "2" },
                    { label: "Attended", value: "1" },
                    { label: "Reviewed", value: "3" },
                    { label: "Not Reviewed", value: "4" },
                  ]}
                  className="input_field"
                  customStyle
                  form={form}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8} md={24} lg={12} className="text-end mb-3">
              <ButtonFilled
                type="primary"
                style={{
                  width: "200px",
                  margin: 0,
                }}
                onClick={() => history.push("/template")}
              >
                <Temp
                  width={"20px"}
                  className="mx-2"
                  style={{ marginTop: "-5px" }}
                />{" "}
                {""}Template
              </ButtonFilled>
            </Col>
          </Row>
        </Form>
      </div>

      <StyledTable
        columns={columns}
        dataSource={filteredData}
        pagination={false}
        scroll={{ y: scrollY }}
        style={{ minHeight: scrollY + 50 }}
        rowClassName={(_, index) => (index % 2 ? "non-white" : "")}
        loading={loading}
        onRow={(record) => {
          return {
            onClick: (e) => {
              if (!record?.attendedon && !record?.reviewedon) {
                e.stopPropagation();
                setKpiDataList(record);
                setIsViewKpi(true);
              } else {
                history.push("/hrreview", { dataList: record, isView: true });
              }
            },
          };
        }}
      />
      <SuccessDeclinePopup
        open={popupOpen}
        setOpen={setPopupOpen}
        type="success"
        message="Deleted"
      />
    </HRPerformanceWrap>
  );
};

export default HRPerformance;
